import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle, Loader2, FileSpreadsheet, HelpCircle } from 'lucide-react';
import { amazonIndexCheckerService } from '@/services/amazonIndexCheckerService';
import { ImportResult, SyncResult, SheetsConfig } from '@/types/amazonIndexChecker';
import { useAuth } from '@/hooks/useAuth';
import { usePMT3Selection } from '@/context/PMT3SelectionContext';

// Regex to extract spreadsheet ID from various Google Sheets URL formats
const extractSpreadsheetId = (input: string): string => {
  const trimmedInput = input.trim();
  
  // If it's already just an ID (no slashes, no protocol), return as-is
  if (!/[\/\\]/.test(trimmedInput) && !trimmedInput.startsWith('http')) {
    return trimmedInput;
  }
  
  // Extract from URL: https://docs.google.com/spreadsheets/d/ID/...
  const urlMatch = trimmedInput.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
  if (urlMatch) {
    return urlMatch[1];
  }
  
  // If no match found, return original input
  return trimmedInput;
};

interface GoogleSheetsImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (result: ImportResult | SyncResult) => void;
}

const GoogleSheetsImportModal: React.FC<GoogleSheetsImportModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [spreadsheetId, setSpreadsheetId] = useState('');
  const [inputValue, setInputValue] = useState(''); // Raw input from user (ID or URL)
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSyncMode, setIsSyncMode] = useState(false);
  const [originalSpreadsheetId, setOriginalSpreadsheetId] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isEditingId, setIsEditingId] = useState(false);
  
  // Spreadsheet confirmation state
  const [spreadsheetTitle, setSpreadsheetTitle] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingSpreadsheetId, setPendingSpreadsheetId] = useState('');

  const { user } = useAuth();
  const { selectedCompany, selectedProduct } = usePMT3Selection();

  // Verify spreadsheet and get title
  const verifySpreadsheet = async (extractedId: string): Promise<string | null> => {
    if (!extractedId.trim()) return null;
    
    try {
      setIsVerifying(true);
      setError(null);
      
      // Use the existing Google Sheets API to get spreadsheet metadata
      const response = await fetch('https://us-central1-jls-apps.cloudfunctions.net/fetchSpreadsheetTitle', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await user?.getIdToken()}`
        },
        body: JSON.stringify({
          data: { spreadsheetId: extractedId }
        })
      });
      
      const result = await response.json();
      if (result.result?.title) {
        return result.result.title;
      } else {
        throw new Error(result.error?.message || 'Failed to fetch spreadsheet title');
      }
    } catch (err: any) {
      console.error('Spreadsheet verification failed:', err);
      setError(err.message || 'Unable to verify spreadsheet. Please check the ID/URL and try again.');
      return null;
    } finally {
      setIsVerifying(false);
    }
  };

  // Check if config exists to determine mode
  React.useEffect(() => {
    const checkConfig = async () => {
      if (selectedCompany && selectedProduct && isOpen) {
        const config = await amazonIndexCheckerService.getSheetsConfig(
          selectedCompany,
          selectedProduct.id
        );
        setIsSyncMode(!!config);
        if (config) {
          setSpreadsheetId(config.spreadsheetId);
          setInputValue(config.spreadsheetId); // Set input value as well
          setOriginalSpreadsheetId(config.spreadsheetId);
        } else {
          setSpreadsheetId('');
          setInputValue('');
          setOriginalSpreadsheetId('');
        }
        setHasUnsavedChanges(false);
        setIsEditingId(false);
        setShowConfirmation(false);
        setSpreadsheetTitle(null);
      }
    };
    checkConfig();
  }, [isOpen, selectedCompany, selectedProduct]);

  const handleUpdateSpreadsheetId = async () => {
    if (!selectedCompany || !selectedProduct || !user?.email || !spreadsheetId.trim()) {
      setError('Please enter a valid spreadsheet ID.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Update the sheets config with new spreadsheet ID
      await amazonIndexCheckerService.updateSheetsConfig(
        selectedCompany,
        selectedProduct.id,
        spreadsheetId.trim(),
        user.email
      );
      
      setOriginalSpreadsheetId(spreadsheetId);
      setHasUnsavedChanges(false);
      setSuccessMessage('Spreadsheet ID updated successfully!');
      
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      setError('Failed to update spreadsheet ID. Please try again.');
      console.error('Update spreadsheet ID error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImportOrSync = async () => {
    if (!selectedCompany || !selectedProduct || !user?.email || !spreadsheetId.trim()) {
      setError('Please select a company, product, and enter a valid spreadsheet ID.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      let result: ImportResult | SyncResult;
      if (isSyncMode) {
        // If spreadsheet ID was changed, update it first
        if (hasUnsavedChanges) {
          await amazonIndexCheckerService.updateSheetsConfig(
            selectedCompany,
            selectedProduct.id,
            spreadsheetId.trim(),
            user.email
          );
          setOriginalSpreadsheetId(spreadsheetId);
          setHasUnsavedChanges(false);
        }
        
        result = await amazonIndexCheckerService.syncSheets(
          selectedCompany,
          selectedProduct.id,
          user.email
        ) as SyncResult;
      } else {
        result = await amazonIndexCheckerService.importFromSheets(
          spreadsheetId.trim(),
          selectedCompany,
          selectedProduct.id,
          user.email
        );
      }

      if (result.success) {
        const hasNewTerms = result.newTerms > 0;
        const successMsg = isSyncMode
          ? hasNewTerms
            ? `Synced successfully! ${result.newTerms} new terms imported, ${result.duplicates} duplicates skipped.`
            : `Sync complete! No new terms found. All ${result.totalTerms} terms were already imported.`
          : `Imported successfully! ${result.newTerms} new terms added, ${result.duplicates} duplicates skipped.`;
        
        setSuccessMessage(successMsg);
        
        // Don't auto-close for sync with 0 new terms - let user read the message and close manually
        if (isSyncMode && !hasNewTerms) {
          // Don't auto-close, let user close manually
        } else {
          // Auto-close for successful imports or syncs with new terms
          setTimeout(() => {
            handleCloseModal();
          }, 3000);
        }
        
        onSuccess(result);
        
        if (!isSyncMode) {
          setSpreadsheetId(''); // Clear for next initial import
        }
      } else {
        setError(result.error || 'Import/Sync failed. Please check the spreadsheet ID and try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Sheets import/sync error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setSpreadsheetId('');
    setOriginalSpreadsheetId('');
    setHasUnsavedChanges(false);
    setIsEditingId(false);
    setError(null);
    setSuccessMessage(null);
    onClose();
  };

  const handleStartEditing = () => {
    setIsEditingId(true);
    setError(null);
    setSuccessMessage(null);
  };

  const handleCancelEditing = () => {
    setSpreadsheetId(originalSpreadsheetId);
    setHasUnsavedChanges(false);
    setIsEditingId(false);
  };

  const handleSaveId = async () => {
    await handleUpdateSpreadsheetId();
    setIsEditingId(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    // Extract spreadsheet ID from input (could be URL or raw ID)
    const extractedId = extractSpreadsheetId(newValue);
    setSpreadsheetId(extractedId);
    setHasUnsavedChanges(isSyncMode && extractedId !== originalSpreadsheetId);
    
    // Clear previous verification state when input changes
    setSpreadsheetTitle(null);
    setShowConfirmation(false);
    setPendingSpreadsheetId('');
  };

  const handleVerifySpreadsheet = async () => {
    const extractedId = extractSpreadsheetId(inputValue);
    if (!extractedId.trim()) {
      setError('Please enter a valid spreadsheet ID or URL.');
      return;
    }

    const title = await verifySpreadsheet(extractedId);
    if (title) {
      setSpreadsheetTitle(title);
      setPendingSpreadsheetId(extractedId);
      setShowConfirmation(true);
    }
  };

  const handleConfirmSpreadsheet = () => {
    setSpreadsheetId(pendingSpreadsheetId);
    setShowConfirmation(false);
    setError(null);
    setHasUnsavedChanges(isSyncMode && pendingSpreadsheetId !== originalSpreadsheetId);
  };

  const handleCancelVerification = () => {
    setSpreadsheetTitle(null);
    setShowConfirmation(false);
    setPendingSpreadsheetId('');
    setInputValue(spreadsheetId); // Reset input to current spreadsheet ID
  };

  const handleSpreadsheetIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSpreadsheetId(newValue);
    setInputValue(newValue); // Keep input value in sync
    setHasUnsavedChanges(isSyncMode && newValue !== originalSpreadsheetId);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCloseModal}>
      <DialogContent className="max-w-2xl w-full mx-auto">
        <DialogHeader className="text-center">
          <DialogTitle className="flex items-center justify-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            {isSyncMode ? 'Sync Sheets Terms' : 'Import Terms from Google Sheets'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-6">
          <div className="space-y-3">
            <Label htmlFor="spreadsheetId" className="text-sm font-medium">
              Google Sheets ID
            </Label>
            
            {isSyncMode && !isEditingId ? (
              // Display mode for sync - show ID with edit button
              <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md">
                <code className="flex-1 text-sm font-mono text-gray-800 dark:text-gray-200 break-all">
                  {spreadsheetId}
                </code>
                <Button
                  onClick={handleStartEditing}
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 hover:bg-gray-200 dark:hover:bg-gray-700"
                  title="Edit Spreadsheet ID"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </Button>
              </div>
            ) : (
              // Edit mode - show input field with verification flow
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    id="spreadsheetId"
                    value={isSyncMode ? spreadsheetId : inputValue}
                    onChange={isSyncMode ? handleSpreadsheetIdChange : handleInputChange}
                    placeholder={isSyncMode ? "Enter the spreadsheet ID" : "Enter spreadsheet ID or paste the full Google Sheets URL"}
                    disabled={isLoading || isVerifying}
                    className="flex-1 bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-foreground"
                  />
                  {!isSyncMode && inputValue.trim() && !showConfirmation && (
                    <Button
                      onClick={handleVerifySpreadsheet}
                      disabled={isVerifying || isLoading}
                      variant="outline"
                      size="sm"
                      className="whitespace-nowrap"
                    >
                      {isVerifying ? (
                        <>
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <HelpCircle className="h-3 w-3 mr-1" />
                          Verify
                        </>
                      )}
                    </Button>
                  )}
                  {isSyncMode && isEditingId && (
                    <>
                      <Button
                        onClick={handleSaveId}
                        disabled={isLoading || !spreadsheetId.trim() || !hasUnsavedChanges}
                        variant="default"
                        size="sm"
                        className="whitespace-nowrap"
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                            Saving...
                          </>
                        ) : (
                          'Save'
                        )}
                      </Button>
                      <Button
                        onClick={handleCancelEditing}
                        disabled={isLoading}
                        variant="outline"
                        size="sm"
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                </div>

                {/* Spreadsheet Confirmation Dialog */}
                {showConfirmation && spreadsheetTitle && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <FileSpreadsheet className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                          Spreadsheet Found
                        </h4>
                        <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
                          <strong>"{spreadsheetTitle}"</strong>
                        </p>
                        <p className="text-xs text-blue-700 dark:text-blue-300 mb-3">
                          Is this the correct spreadsheet you want to import from?
                        </p>
                        <div className="flex gap-2">
                          <Button
                            onClick={handleConfirmSpreadsheet}
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Yes, Use This Sheet
                          </Button>
                          <Button
                            onClick={handleCancelVerification}
                            variant="outline"
                            size="sm"
                            className="border-blue-300 text-blue-700 hover:bg-blue-100"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {successMessage && (
            <Alert className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
              <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
              <AlertDescription className="text-green-700 dark:text-green-300">
                {successMessage}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCloseModal} disabled={isLoading || isVerifying}>
            Cancel
          </Button>
          <Button
            onClick={handleImportOrSync}
            disabled={!spreadsheetId.trim() || isLoading || isVerifying || (!isSyncMode && showConfirmation)}
            className="flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {isSyncMode ? 'Syncing...' : 'Importing...'}
              </>
            ) : (
              <>
                <FileSpreadsheet className="h-4 w-4" />
                {isSyncMode ? 'Sync Terms' : 'Import Terms'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GoogleSheetsImportModal;